import { createFileRoute, notFound } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";
import { PROPERTY_FAQ_CATEGORIES, getPropertyCategoryDef } from "@/content/property-corpus/faq-defs";

export const Route = createFileRoute("/en/property/faq/$category")({
  loader: async ({ params }) => {
    const def = getPropertyCategoryDef(params.category);
    if (!def) throw notFound();
    const { getPropertyFaqsByCategoryEn } = await import("@/content/property-corpus");
    return { def, faqs: getPropertyFaqsByCategoryEn(def.slug) };
  },
  component: PropertyFaqCategoryPageEn,
  head: ({ params, loaderData }) => {
    if (!loaderData) return { meta: [{ title: "Category not found" }, { name: "robots", content: "noindex" }] };
    const site = getActiveSiteUrl();
    const { def, faqs } = loaderData;
    return {
      ...pageHeadForProvince({
        title: `${def.titleEn} — FAQ (${faqs.length})`,
        description: def.descEn,
        path: `/en/property/faq/${params.category}`,
        type: "article",
        alternates: [{ lang: "th", path: `/property/faq/${params.category}` }],
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "Home", url: `${site}/en` },
              { name: "Property Ownership FAQ", url: `${site}/en/property/faq` },
              { name: def.titleEn, url: `${site}/en/property/faq/${params.category}` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqs)) },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema(`/en/property/faq/${params.category}`)) },
      ],
    };
  },
});

function PropertyFaqCategoryPageEn() {
  const { def, faqs } = Route.useLoaderData();
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/en" className="hover:underline">Home</a> <span>/</span>{" "}
          <a href="/en/property/faq" className="hover:underline">Property Ownership FAQ</a> <span>/</span> <span>{def.titleEn}</span>
        </nav>
        <h1 className="mb-3 text-3xl font-bold md:text-4xl">{def.titleEn}</h1>
        <p className="mb-6 text-muted-foreground">{def.descEn}</p>
        <div className="space-y-4">
          {faqs.map((item: { q: string; a: string }, i: number) => (
            <details key={i} className="rounded-lg border bg-card p-4" {...(i < 5 ? { open: true } : {})}>
              <summary className="cursor-pointer font-semibold">{item.q}</summary>
              <p className="faq-answer mt-3 text-sm leading-relaxed text-muted-foreground">{item.a}</p>
            </details>
          ))}
        </div>
        <section className="mt-12">
          <h2 className="mb-3 text-xl font-bold">Other topics</h2>
          <div className="flex flex-wrap gap-2 text-xs">
            {PROPERTY_FAQ_CATEGORIES.filter((c) => c.slug !== def.slug).map((c) => (
              <a key={c.slug} href={`/en/property/faq/${c.slug}`} className="rounded-full border bg-muted/40 px-3 py-1 text-muted-foreground hover:bg-muted">
                {c.titleEn}
              </a>
            ))}
          </div>
        </section>
        <section className="mt-10 rounded-lg border bg-primary/5 p-6">
          <h2 className="mb-2 text-xl font-bold">Talk to our team</h2>
          <p className="mb-4 text-sm text-muted-foreground">
            Document lists and Land Department steps by phone, LINE or email. Officer discretion always applies.
          </p>
          <a href="/contact" className="inline-block rounded-md bg-primary px-5 py-2 font-medium text-primary-foreground">Contact the team →</a>
        </section>
        <FaqSourceNote topic="property" locale="en" />
      </main>
      <SiteFooter />
    </>
  );
}
